import { useEffect } from 'react';
import { Settings, Globe, Clock, DollarSign } from 'lucide-react';
import { Select } from '@/core/ui/components';
import type { WizardFormData } from '../types';

interface SettingsStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  onValidChange: (isValid: boolean) => void;
  errors: Record<string, string>;
}

const TIMEZONE_OPTIONS = [
  { value: 'America/Mexico_City', label: 'Mexico City (GMT-6)' },
  { value: 'America/New_York', label: 'New York (GMT-5)' },
  { value: 'America/Chicago', label: 'Chicago (GMT-6)' },
  { value: 'America/Denver', label: 'Denver (GMT-7)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8)' },
  { value: 'America/Bogota', label: 'Bogota (GMT-5)' },
  { value: 'America/Lima', label: 'Lima (GMT-5)' },
  { value: 'America/Sao_Paulo', label: 'Sao Paulo (GMT-3)' },
  { value: 'Europe/London', label: 'London (GMT+0)' },
  { value: 'Europe/Madrid', label: 'Madrid (GMT+1)' },
  { value: 'Europe/Paris', label: 'Paris (GMT+1)' },
];

const LANGUAGE_OPTIONS = [
  { value: 'es', label: 'Spanish (Español)' },
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Portuguese (Português)' },
];

const CURRENCY_OPTIONS = [
  { value: 'MXN', label: 'Mexican Peso (MXN)' },
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'COP', label: 'Colombian Peso (COP)' },
  { value: 'PEN', label: 'Peruvian Sol (PEN)' },
  { value: 'BRL', label: 'Brazilian Real (BRL)' },
];

export function SettingsStep({ formData, onChange, onValidChange }: SettingsStepProps) {
  useEffect(() => {
    // Settings have defaults, always valid
    onValidChange(true);
  }, [onValidChange]);

  const handleSettingsChange = (field: keyof typeof formData.settings, value: string) => {
    onChange({
      settings: {
        ...formData.settings,
        [field]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
          <Settings className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-theme">Settings</h3>
          <p className="text-sm text-theme-secondary">Configure regional and display preferences</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-theme-tertiary" />
            <label className="text-sm font-medium text-theme">Timezone</label>
          </div>
          <Select
            value={formData.settings.timezone}
            onChange={(e) => handleSettingsChange('timezone', e.target.value)}
            options={TIMEZONE_OPTIONS}
          />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-4 h-4 text-theme-tertiary" />
            <label className="text-sm font-medium text-theme">Language</label>
          </div>
          <Select
            value={formData.settings.language}
            onChange={(e) => handleSettingsChange('language', e.target.value)}
            options={LANGUAGE_OPTIONS}
          />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-theme-tertiary" />
            <label className="text-sm font-medium text-theme">Currency</label>
          </div>
          <Select
            value={formData.settings.currency}
            onChange={(e) => handleSettingsChange('currency', e.target.value)}
            options={CURRENCY_OPTIONS}
          />
        </div>
      </div>

      <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
        <p className="text-sm text-theme-secondary">
          These settings will determine how dates, times, and currency values are displayed throughout the platform for this tenant.
        </p>
      </div>
    </div>
  );
}
